@if ((periodCloseComponent.loading$ | async) === true || loading()) {
  <div class="spinner m-auto my-40 h-32 w-32 border-8"></div>
} @else {
  @if (budgetTemplateUploadProgressId()) {
    <aux-progress-tracker
      [trackerId]="budgetTemplateUploadProgressId()"
      (trackerIdChanged)="onTrackerIdChanged($event)"
      validationInProgressCaption="Import Vendor Estimate Template - Validation Progress"
      dataLoadInProgressCaption="Import Vendor Estimate Template - Data Load Progress"
      class="block"
    />
  }

  <div class="text-2xl font-bold text-black">Expense Detail</div>

  @if (!hasMultipleCurrencies() && gridData().length > 0 && flags().showExpenseDetailAnalytics) {
    <div class="flex gap-[21px]">
      <analytic-card
        [title]="expenseLtdTitle()"
        [value]="expenseLtdTotalFormatted()"
        [showCheckbox]="true"
        checkboxLabel="Show activities exceeding contracted budget"
        [checkboxValue]="overBudgetFilterValue()"
        (checkboxValueChange)="handleOverBudgetChecked($event)"
        class="flex-1"
        pendoTag="expense-detail-analytics-show-activities-exceeding-contracted-budget"
      />

      <analytic-card
        class="flex-1"
        title="Monthly Expense incl. Historical Adjustment"
        [value]="monthlyExpenseTotalFormatted()"
        [showTrend]="true"
        [trendConfig]="monthlyExpenseTrendConfig()"
      />
    </div>

    <div class="flex gap-[21px]">
      <summary-accordion
        columnHeader1="Source"
        columnHeader2="# Activities"
        columnHeader3="Monthly Expense"
        title="Expense Source Summary"
        [rows]="expenseSourceSummaryRows()"
        (rowChecked)="handleExpenseSourceRowChecked($event)"
        localStorageKey="quarter_close_adjustments_expense_source_collapsed"
        class="flex-1"
      />

      <summary-accordion
        columnHeader1="Activities"
        columnHeader2="# Activities"
        columnHeader3="Monthly Expense"
        title="Notes Summary"
        [rows]="notesSummaryRows()"
        (rowChecked)="handleNotesRowChecked($event)"
        localStorageKey="quarter_close_adjustments_notes_collapsed"
        class="flex-1"
      />
    </div>
  }

  <aux-table-panel class="adjustment-panel" [classNames]="'border-b-0'">
    <div class="flex flex-col space-y-2 p-[15px]">
      <div class="flex justify-end">
        <div class="flex items-center space-x-4">
          @if (gridApi$ | async; as gridApi) {
            <aux-column-chooser
              [gridApi]="gridApi"
              [configKey]="columnChooserConfigName()"
              [columnMapper]="columnChooserGrouping"
              [disabled]="editMode()"
              [excludeColumns]="excludeColumnChooserColumns()"
              [oneColumnWidthStrategy]="oneColumnWidthStrategy"
              [showPinIcons]="true"
              (afterColumnChooserApplied)="onAfterColumnChooserApplied()"
            />
          }

          <div class="flex">
            <aux-input
              placeholder="Search"
              [formControl]="search"
              iconPosition="left"
              icon="Search"
              class="w-[273px]"
              [inputClassName]="'h-[40px]'"
              hotKey="/"
              #searchInput
            />
          </div>

          <div class="d-block spacing h-10 border-r"></div>

          <aux-export-excel-button
            [clickFN]="onExport.bind(this)"
            [spinnerSize]="7"
            pendoTag="quarter-close-adjustments-excel-export-button"
          />

          @if ((editMode$ | async) === false) {
            <div
              *auxAuthorize="{
                permissions: [
                  'PERMISSION_MODIFY_OPEN_MONTH_ADJUSTMENTS',
                  'PERMISSION_EDIT_VENDOR_ESTIMATE',
                ],
              }"
              [auxTooltip]="
                editButtonReasonsToDisabled().length === 1 ? editButtonReasonsToDisabled()[0] : ''
              "
              [template]="
                editButtonReasonsToDisabled().length > 1
                  ? reasonsToDisableEditButtonTemplate
                  : undefined
              "
            >
              <aux-button
                [disabled]="
                  (editButtonDisabled$ | async) === true ||
                  expandingRows() ||
                  (allEditableColumnsHidden$ | async) === true
                "
                (click)="onEditMode()"
                variant="primary"
                icon="Pencil"
                label="Edit"
              />
            </div>

            <ng-template #reasonsToDisableEditButtonTemplate>
              <aux-errors-tooltip [errors]="editButtonReasonsToDisabled()" />
            </ng-template>
          }

          @if ((editMode$ | async) === true) {
            <ng-container>
              <aux-button
                variant="secondary"
                icon="X"
                label="Cancel"
                (click)="onCancel()"
                [disabled]="expandingRows()"
              />
              @if ((editMode$ | async) === true) {
                <aux-button
                  label="Save"
                  variant="success"
                  [disabled]="rows.edited.size === 0 || expandingRows()"
                  [loading]="expandingRows()"
                  [auxTooltip]="
                    rows.edited.size === 0 ? 'You need to make one or more changes to save' : ''
                  "
                  [onClick]="onSave.bind(this)"
                  icon="Check"
                />
              }
            </ng-container>
          }

          <div>
            <aux-menu
              #forecast_down_up_load_menu
              [showMenuForExternal]="permissions().vendorEstimateUpload"
              [disabled]="isUploadVendorEstimateDisabled()"
              [auxTooltip]="
                isUploadVendorEstimateDisabled()
                  ? 'Vendor Contract(s) selected do not provide Vendor Estimate. Update on Vendor Contracts page if needed.'
                  : ''
              "
            >
              <button
                type="button"
                role="menuitem"
                class="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                (click)="onVendorEstimateUploadClick(); forecast_down_up_load_menu.close()"
              >
                <aux-icon name="Upload" />
                <span class="ml-2">Upload Vendor Estimate</span>
              </button>
            </aux-menu>
          </div>
        </div>
      </div>
      <div class="flex justify-start">
        <div class="flex items-center space-x-2">
          <ng-select
            #monthSelect
            class="month-select w-52"
            [formControl]="selected_month"
            [clearable]="false"
            [items]="months"
            bindLabel="label"
            bindValue="iso"
            dropdownPosition="bottom"
          >
            <ng-template let-item="item" ng-option-tmp>
              <div (click)="selectMonth($event, item.iso)" class="option-wrapper text-sm">
                {{ item.label }}
              </div>
            </ng-template>
          </ng-select>

          @if (multipleVendorsActive()) {
            <aux-multiple-vendor-select
              class="w-[480px]"
              [(appliedSelectedVendorIDs)]="selectedVendorIDs"
              (applySelection)="onApplySelection($event)"
              [excludeNoBudgetVendors]="true"
            />
          } @else {
            <aux-vendor-dropdown
              [formControl]="selected_vendor"
              [vendors]="(vendors$ | async)!"
              [beforeSelect]="beforeFilterChanges((saveCheck$ | async)!!)"
              (onChange)="onCategoryDropdownChange()"
              [width]="260"
            />
          }

          <ng-select
            class="w-48"
            bindLabel="label"
            bindValue="value"
            placeholder="Cost Category: All"
            [class.show-placeholder]="selectedCategory() === ''"
            [formControl]="selected_category"
            [clearable]="false"
            [items]="categories"
            dropdownPosition="bottom"
          />
          <ng-select
            class="w-44"
            placeholder="Materiality: All"
            bindLabel="label"
            bindValue="value"
            [class.show-placeholder]="selectedThreshold() === ''"
            [formControl]="selected_threshold"
            [clearable]="false"
            [items]="materialityThresholds"
            dropdownPosition="bottom"
          />
          <aux-button variant="hyperlink" label="Clear All" (click)="onClearFilter()" />
        </div>
      </div>
    </div>

    @if (gridUpdated && !vendorEstimatesLoading()) {
      <div
        auxStickyFixedHeightGrid
        (sticked)="onSticked($event)"
        [offset]="BOTTOM_CONTAINER_PADDING"
      >
        <ag-grid-angular
          id="ima_table"
          style="max-width: 100%"
          [ngStyle]="{
            height: tableHeight() + 'px',
            boxShadow: '1px 0 0 0 var(--aux-gray-dark), -1px 0 0 0 var(--aux-gray-dark)',
          }"
          class="new-flat-theme w-full overflow-x-hidden overscroll-contain rounded-b-[8px] tabular-nums"
          [quickFilterText]="search.value ?? ''"
          fillHandleDirection="y"
          [gridOptions]="(gridOptions$ | async)!"
          [enableFillHandle]="(editMode$ | async) === true"
          [groupAllowUnbalanced]="true"
          (gridReady)="onGridReady($event)"
          (cellValueChanged)="onCellValueChanged($event)"
          (filterChanged)="onFilterChanged()"
          (firstDataRendered)="onFirstDataRendered()"
          [overlayNoRowsTemplate]="showCantEdit ? cantEditMessage : undefined"
          [overlayLoadingTemplate]="overlayLoadingTemplate"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          auxGridColumnWidthPersistence="quarter-close-adjustments-grid"
        />
      </div>
    }
  </aux-table-panel>
}
